import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UpgradeModal({ isOpen, onClose }) {
  const { currentUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUpgrade = async () => {
    if (!currentUser) {
      toast.error('Please log in to upgrade');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;
      
      // Create checkout session
      const response = await axios.post(`${API}/payments/checkout`, null, {
        params: {
          user_id: currentUser.uid,
          origin_url: originUrl
        }
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white max-w-2xl w-full p-8 relative shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-2 text-gray-900">Upgrade to Pro</h2>
          <p className="text-gray-600">Unlock powerful AI features for professional podcast production</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Free Plan */}
          <div className="border-2 border-gray-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-2 text-gray-900">Free</h3>
            <p className="text-3xl font-bold mb-4 text-gray-900">£0<span className="text-lg font-normal text-gray-600">/month</span></p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">Record unlimited podcasts</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">Live teleprompter</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">Manual transcripts</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">Basic downloads (MP4, MP3, TXT)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">Add intro/outro clips</span>
              </li>
            </ul>
          </div>

          {/* Pro Plan */}
          <div className="border-2 border-blue-500 rounded-lg p-6 bg-gradient-to-br from-blue-50 to-purple-50 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-semibold">
              RECOMMENDED
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-900">Pro</h3>
            <p className="text-3xl font-bold mb-4 text-gray-900">£9.99<span className="text-lg font-normal text-gray-600">/month</span></p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-900 font-medium">Everything in Free, plus:</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-900"><strong>AI Audio Enhancement</strong> - Remove noise, optimize levels</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-900"><strong>AI Video Enhancement</strong> - Improve lighting, stabilize</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-900"><strong>AI Transcript Editing</strong> - Remove filler words instantly</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-900">Priority support</span>
              </li>
            </ul>
            <Button 
              onClick={handleUpgrade}
              disabled={isProcessing}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
            >
              {isProcessing ? 'Redirecting to Checkout...' : 'Upgrade to Pro'}
            </Button>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500">
          💳 Secure payment powered by Stripe. Cancel anytime.
        </p>
      </Card>
    </div>
  );
}
